import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import axios from "axios";
import dayjs from "dayjs";
import $javelink_api from "../../api/services/javelink_api";
import {errorMessage} from "../../api/helpers/scripts";
import {created_at_before} from "../../pages/Authorized/Statistics/IOS/IOSClicksStats/IOSClicksStats";
import {ABORTED_ERROR, IOS} from "../../utils/vars/staticVariables";

// Для зберігання порядку користувачів
let androidUserOrder = [];
let iosUserOrder = [];
let androidMonthUserOrder = [];
let iosMonthUserOrder = [];

// Зберігання всіх відомих користувачів з їх даними
let androidWeekUsers = {};
let iosWeekUsers = {};
let androidMonthUsers = {};
let iosMonthUsers = {};

// Для синхронізації даних між таблицями
let androidWeekTablesLoaded = {
    androidDataWeek1List: false,
    androidDataWeek2List: false,
    androidDataWeek3List: false,
    androidDataWeek4List: false
};

let iosWeekTablesLoaded = {
    iosDataWeek1List: false,
    iosDataWeek2List: false,
    iosDataWeek3List: false,
    iosDataWeek4List: false
};

let androidMonthTablesLoaded = {
    androidDataMonth1List: false,
    androidDataMonth2List: false,
    androidDataMonth3List: false,
    androidDataMonth4List: false
};

let iosMonthTablesLoaded = {
    iosDataMonth1List: false,
    iosDataMonth2List: false,
    iosDataMonth3List: false,
    iosDataMonth4List: false
};

// Зберігання результатів для можливого перезавантаження
let androidWeekResults = {};
let iosWeekResults = {};
let androidMonthResults = {};
let iosMonthResults = {};

export const fetchTeamsWeeksAnalytics = createAsyncThunk(
    "clientAnalytics/fetchTeamsWeeksAnalytics",
    async ({os, params, state}, {signal, rejectWithValue, dispatch, getState}) => {
        try {
            const {data} = await $javelink_api.get(
                `/click-data/install-statistic/${os}/?` + params,
                {
                    signal,
                },
            )

            // Визначаємо об'єкти для роботи з даними
            const usersCollection = os === 'android' ? androidWeekUsers : iosWeekUsers;
            const tablesLoaded = os === 'android' ? androidWeekTablesLoaded : iosWeekTablesLoaded;
            const resultsCollection = os === 'android' ? androidWeekResults : iosWeekResults;

            // Позначаємо поточну таблицю як завантажену
            tablesLoaded[state] = true;

            // Зберігаємо оригінальні результати
            resultsCollection[state] = [...data.results];

            // Зберігаємо інформацію про всіх користувачів
            data.results.forEach(item => {
                usersCollection[item.user] = {
                    user: item.user,
                    top_user: item.top_user,
                    color: item.color
                };
            });

            // Зберігаємо порядок користувачів з першого тижня
            if (state.includes('1')) {
                const userOrderList = data.results.map(item => item.user);
                if (os === 'android') {
                    androidUserOrder = userOrderList;
                } else if (os === 'ios') {
                    iosUserOrder = userOrderList;
                }
            }

            // Додаємо до результатів усіх відомих користувачів
            const existingUsers = new Set(data.results.map(item => item.user));
            Object.values(usersCollection).forEach(item => {
                if (!existingUsers.has(item.user)) {
                    // Додаємо запис з нульовими значеннями для користувача
                    data.results.push({
                        user: item.user,
                        top_user: item.top_user,
                        color: item.color,
                        installs: 0
                    });
                }
            });

            // Сортуємо дані
            const userOrder = os === 'android' ? androidUserOrder : iosUserOrder;
            if (userOrder.length > 0) {
                data.results = data.results.sort((a, b) => {
                    const indexA = userOrder.indexOf(a.user);
                    const indexB = userOrder.indexOf(b.user);

                    // Якщо користувач не знайдений в порядку першого тижня, розмістити його в кінці
                    if (indexA === -1 && indexB === -1) {
                        // Сортування за алфавітом для нових користувачів
                        return a.user.localeCompare(b.user);
                    }
                    if (indexA === -1) return 1;
                    if (indexB === -1) return -1;

                    return indexA - indexB;
                });
            }

            // Оновлюємо всі інші таблиці, якщо вони вже завантажені
            const weekStateKeys = Object.keys(tablesLoaded);
            for (const weekState of weekStateKeys) {
                if (weekState !== state && tablesLoaded[weekState]) {
                    const stateResults = [...resultsCollection[weekState]];
                    const stateExistingUsers = new Set(stateResults.map(item => item.user));

                    // Додаємо нових користувачів
                    Object.values(usersCollection).forEach(item => {
                        if (!stateExistingUsers.has(item.user)) {
                            stateResults.push({
                                user: item.user,
                                top_user: item.top_user,
                                color: item.color,
                                installs: 0
                            });
                        }
                    });

                    // Сортуємо та оновлюємо дані в Redux
                    stateResults.sort((a, b) => {
                        const indexA = userOrder.indexOf(a.user);
                        const indexB = userOrder.indexOf(b.user);

                        if (indexA === -1 && indexB === -1) {
                            return a.user.localeCompare(b.user);
                        }
                        if (indexA === -1) return 1;
                        if (indexB === -1) return -1;

                        return indexA - indexB;
                    });

                    // Оновлюємо стан Redux напряму
                    const clientAnalytics = getState().clientAnalytics;
                    dispatch({
                        type: "clientAnalytics/updateWeekData",
                        payload: {
                            state: weekState,
                            data: stateResults
                        }
                    });
                }
            }

            return {data, state, os}

        } catch (error) {
            let err = errorMessage(error);

            if (axios.isCancel(error)) {
                return rejectWithValue();
            }

            throw new Error(err);
        }
    },
);

export const fetchRate = createAsyncThunk(
    "clientAnalytics/fetchIosRate",
    async (
        {os},
        {signal, rejectWithValue}) => {
        try {


            const {data} = await $javelink_api.get(
                `/click-data/install-statistic/${os}/rate?`
                ,
                {
                    signal,
                },
            )
            return {data, os}

        } catch (error) {
            let err = errorMessage(error);

            if (axios.isCancel(error)) {
                return rejectWithValue();
            }

            throw new Error(err);
        }
    },
);

export const fetchTeamsMonthsAnalytics = createAsyncThunk(
    "clientAnalytics/fetchTeamsMonthsAnalytics",
    async ({os, params, state}, {signal, rejectWithValue, dispatch, getState}) => {
        try {
            const {data} = await $javelink_api.get(
                `/click-data/install-statistic/${os}/?` + params,
                {
                    signal,
                },
            )

            // Визначаємо об'єкти для роботи з даними
            const usersCollection = os === 'android' ? androidMonthUsers : iosMonthUsers;
            const tablesLoaded = os === 'android' ? androidMonthTablesLoaded : iosMonthTablesLoaded;
            const resultsCollection = os === 'android' ? androidMonthResults : iosMonthResults;

            // Позначаємо поточну таблицю як завантажену
            tablesLoaded[state] = true;

            // Зберігаємо оригінальні результати
            resultsCollection[state] = [...data.results];

            // Зберігаємо інформацію про всіх користувачів
            data.results.forEach(item => {
                usersCollection[item.user] = {
                    user: item.user,
                    top_user: item.top_user,
                    color: item.color
                };
            });

            // Зберігаємо порядок користувачів з першого місяця
            if (state.includes('1')) {
                const userOrderList = data.results.map(item => item.user);
                if (os === 'android') {
                    androidMonthUserOrder = userOrderList;
                } else if (os === 'ios') {
                    iosMonthUserOrder = userOrderList;
                }
            }

            // Додаємо до результатів усіх відомих користувачів
            const existingUsers = new Set(data.results.map(item => item.user));
            Object.values(usersCollection).forEach(item => {
                if (!existingUsers.has(item.user)) {
                    // Додаємо запис з нульовими значеннями для користувача
                    data.results.push({
                        user: item.user,
                        top_user: item.top_user,
                        color: item.color,
                        installs: 0
                    });
                }
            });

            // Сортуємо дані
            const userOrder = os === 'android' ? androidMonthUserOrder : iosMonthUserOrder;
            if (userOrder.length > 0) {
                data.results = data.results.sort((a, b) => {
                    const indexA = userOrder.indexOf(a.user);
                    const indexB = userOrder.indexOf(b.user);

                    // Якщо користувач не знайдений в порядку першого місяця, розмістити його в кінці
                    if (indexA === -1 && indexB === -1) {
                        // Сортування за алфавітом для нових користувачів
                        return a.user.localeCompare(b.user);
                    }
                    if (indexA === -1) return 1;
                    if (indexB === -1) return -1;

                    return indexA - indexB;
                });
            }

            // Оновлюємо всі інші таблиці, якщо вони вже завантажені
            const monthStateKeys = Object.keys(tablesLoaded);
            for (const monthState of monthStateKeys) {
                if (monthState !== state && tablesLoaded[monthState]) {
                    const stateResults = [...resultsCollection[monthState]];
                    const stateExistingUsers = new Set(stateResults.map(item => item.user));

                    // Додаємо нових користувачів
                    Object.values(usersCollection).forEach(item => {
                        if (!stateExistingUsers.has(item.user)) {
                            stateResults.push({
                                user: item.user,
                                top_user: item.top_user,
                                color: item.color,
                                installs: 0
                            });
                        }
                    });

                    // Сортуємо та оновлюємо дані в Redux
                    stateResults.sort((a, b) => {
                        const indexA = userOrder.indexOf(a.user);
                        const indexB = userOrder.indexOf(b.user);

                        if (indexA === -1 && indexB === -1) {
                            return a.user.localeCompare(b.user);
                        }
                        if (indexA === -1) return 1;
                        if (indexB === -1) return -1;

                        return indexA - indexB;
                    });

                    // Оновлюємо стан Redux напряму
                    dispatch({
                        type: "clientAnalytics/updateMonthData",
                        payload: {
                            state: monthState,
                            data: stateResults
                        }
                    });
                }
            }

            return {data, state}

        } catch (error) {
            let err = errorMessage(error);

            if (axios.isCancel(error)) {
                return rejectWithValue();
            }

            throw new Error(err);
        }
    },
);

const clientAnalyticsSlice = createSlice({
    name: "clientAnalytics",
    initialState: {
        iosDataWeekPending: false,
        iosDataWeek1List: [],
        iosDataWeek2List: [],
        iosDataWeek3List: [],
        iosDataWeek4List: [],
        iosDataMonth1List: [],
        iosDataMonth2List: [],
        iosDataMonth3List: [],
        iosDataMonth4List: [],
        iosDataMonthPending: false,

        iosTop10Customers: [],
        iosBottom10Customers: [],
        iosTopCustomersPending: false,

        androidDataWeekPending: false,
        androidDataWeek1List: [],
        androidDataWeek2List: [],
        androidDataWeek3List: [],
        androidDataWeek4List: [],

        androidDataMonth1List: [],
        androidDataMonth2List: [],
        androidDataMonth3List: [],
        androidDataMonth4List: [],
        androidDataMonthPending: false,

        androidTop10Customers: [],
        androidBottom10Customers: [],
        androidTopCustomersPending: false,
    },
    reducers: {
        resetWeeksDataLists: (state, {payload}) => {
            // Скидаємо збережені дані
            if (payload === 'android') {
                androidUserOrder = [];
                androidWeekUsers = {};
                androidWeekTablesLoaded = {
                    androidDataWeek1List: false,
                    androidDataWeek2List: false,
                    androidDataWeek3List: false,
                    androidDataWeek4List: false
                };
                androidWeekResults = {};
            } else if (payload === 'ios') {
                iosUserOrder = [];
                iosWeekUsers = {};
                iosWeekTablesLoaded = {
                    iosDataWeek1List: false,
                    iosDataWeek2List: false,
                    iosDataWeek3List: false,
                    iosDataWeek4List: false
                };
                iosWeekResults = {};
            }

            state[payload + 'DataWeek1List'] = [];
            state[payload + 'DataWeek2List'] = [];
            state[payload + 'DataWeek3List'] = [];
            state[payload + 'DataWeek4List'] = [];
        },
        resetMonthsDataLists: (state, {payload}) => {
            // Скидаємо збережені дані для місяців
            if (payload === 'android') {
                androidMonthUserOrder = [];
                androidMonthUsers = {};
                androidMonthTablesLoaded = {
                    androidDataMonth1List: false,
                    androidDataMonth2List: false,
                    androidDataMonth3List: false,
                    androidDataMonth4List: false
                };
                androidMonthResults = {};
            } else if (payload === 'ios') {
                iosMonthUserOrder = [];
                iosMonthUsers = {};
                iosMonthTablesLoaded = {
                    iosDataMonth1List: false,
                    iosDataMonth2List: false,
                    iosDataMonth3List: false,
                    iosDataMonth4List: false
                };
                iosMonthResults = {};
            }

            state[payload + 'DataMonth1List'] = [];
            state[payload + 'DataMonth2List'] = [];
            state[payload + 'DataMonth3List'] = [];
            state[payload + 'DataMonth4List'] = [];
        },
        resetRateData: (state, payload) => {
            state[payload + 'Top10Customers'] = [];
            state[payload + 'Bottom10Customers'] = [];
        },
        updateWeekData: (state, {payload}) => {
            const {state: weekState, data} = payload;
            state[weekState] = data;
        },
        updateMonthData: (state, {payload}) => {
            const {state: monthState, data} = payload;
            state[monthState] = data;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchRate.pending, (state, {meta}) => {
                const {os} = meta.arg

                state[os + 'TopCustomersPending'] = true;
                state[os + 'Top10Customers'] = [];
                state[os + 'Bottom10Customers'] = [];
            })
            .addCase(fetchRate.fulfilled, (state, {payload, meta}) => {
                const {os} = meta.arg;
                const {bottom10, top10} = payload?.data

                state[os + 'TopCustomersPending'] = false;
                state[os + 'Top10Customers'] = top10;
                state[os + 'Bottom10Customers'] = bottom10;
            })
            .addCase(fetchRate.rejected, (state, {meta}) => {
                const {os} = meta.arg;

                state[os + 'TopCustomersPending'] = true;
                state[os + 'Top10Customers'] = [];
                state[os + 'Bottom10Customers'] = [];
            })

            .addCase(fetchTeamsMonthsAnalytics.pending, (state, {meta}) => {
                const {state: monthState, os} = meta.arg;

                if (monthState.includes('1')) {
                    state[os + 'DataMonthPending'] = true;
                }
                state[monthState] = [];
            })
            .addCase(fetchTeamsMonthsAnalytics.fulfilled, (state, {payload, meta}) => {
                const {state: monthState, data} = payload;
                const {os} = meta.arg;

                if (monthState.includes('4')) {
                    state[os + 'DataMonthPending'] = false;
                }
                state[monthState] = data.results;
            })
            .addCase(fetchTeamsMonthsAnalytics.rejected, (state, {meta}) => {
                const {state: monthState, os} = meta.arg;
                if (monthState.includes('4')) {
                    state[os + 'DataMonthPending'] = false;
                }
                state[monthState] = [];
            })

            .addCase(fetchTeamsWeeksAnalytics.pending, (state, {meta}) => {
                const {state: weekState, os} = meta.arg;

                if (weekState.includes('1')) {
                    state[os + 'DataWeekPending'] = true;
                }

                state[weekState] = [];
            })
            .addCase(fetchTeamsWeeksAnalytics.fulfilled, (state, {payload, meta}) => {
                const {state: weekState, data} = payload;
                const {os} = meta.arg;

                if (weekState.includes('4')) {
                    state[os + 'DataWeekPending'] = false;
                }
                state[weekState] = data.results;
            })
            .addCase(fetchTeamsWeeksAnalytics.rejected, (state, {meta}) => {
                const {state: weekState, os} = meta.arg;
                if (weekState.includes('4')) {
                    state[os + 'DataWeekPending'] = false;
                }
                state[weekState] = [];
            })
    },
});

export const {
    resetWeeksDataLists,
    resetMonthsDataLists,
    resetRateData,
    updateWeekData,
    updateMonthData
} = clientAnalyticsSlice.actions;

export default clientAnalyticsSlice.reducer;

export const clientAnalyticsSelector = (state) => state.clientAnalytics;


